"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const NAMESPACE = "new-sla-stereolithography";

const IMAGE_BASE = "/image/new-version/more/SLA/Surface-Finish-Options";

const FINISH_ROWS = [
	{ keyPrefix: "surfaceFinishRow1" },
	{ keyPrefix: "surfaceFinishRow2" },
	{ keyPrefix: "surfaceFinishRow3" },
	{ keyPrefix: "surfaceFinishRow4" },
] as const;

const SAMPLES = [
	{ keyPrefix: "surfaceFinishSample1", image: `${IMAGE_BASE}/abs-unfinished.webp`, highlight: false },
	{ keyPrefix: "surfaceFinishSample2", image: `${IMAGE_BASE}/abs-standard.webp`, highlight: false },
	{ keyPrefix: "surfaceFinishSample3", image: `${IMAGE_BASE}/abs-natural.webp`, highlight: false },
	{ keyPrefix: "surfaceFinishSample4", image: `${IMAGE_BASE}/abs-custom.webp`, highlight: false },
	{
		keyPrefix: "surfaceFinishSample5",
		image: `${IMAGE_BASE}/microfine-gray-unfinished.webp`,
		highlight: false,
	},
	{
		keyPrefix: "surfaceFinishSample6",
		image: `${IMAGE_BASE}/microfine-gray-standard.webp`,
		highlight: false,
	},
	{ keyPrefix: "surfaceFinishSample7", image: `${IMAGE_BASE}/microfine-gray-natural.webp`, highlight: false },
	{ keyPrefix: "surfaceFinishSample8", image: `${IMAGE_BASE}/microfine-gray-custom.webp`, highlight: false },
] as const;

export default function SurfaceFinishOptions() {
	const t = useTranslations(NAMESPACE);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
					{t("surfaceFinishTitleLine1")}
					<br />
					{t("surfaceFinishTitleLine2")}
				</h2>

				{/* Finish Level Table */}
				<div className="mb-8 overflow-x-auto sm:mb-10 md:mb-12">
					<table className="w-full min-w-[640px] border-collapse">
						<thead>
							<tr className="border-b border-[#e7e7e7]">
								<th className="w-[18%] px-4 py-3 text-left text-sm font-bold text-black md:px-6 md:py-4 md:text-base">
									{t("surfaceFinishColFinishLevel")}
								</th>
								<th className="w-[42%] px-4 py-3 text-left text-sm font-bold text-black md:px-6 md:py-4 md:text-base">
									{t("surfaceFinishColProcessDesc")}
								</th>
								<th className="w-[40%] px-4 py-3 text-left text-sm font-bold text-black md:px-6 md:py-4 md:text-base">
									{t("surfaceFinishColAppScenarios")}
								</th>
							</tr>
						</thead>
						<tbody>
							{FINISH_ROWS.map((row) => (
								<tr key={row.keyPrefix} className="border-b border-[#e7e7e7]">
									<td className="px-4 py-4 align-top text-sm font-bold text-black md:px-6 md:py-5 md:text-base">
										{t(`${row.keyPrefix}Level`)}
									</td>
									<td className="px-4 py-4 align-top text-sm leading-relaxed text-gray-700 md:px-6 md:py-5 md:text-base">
										{t(`${row.keyPrefix}Desc`)}
									</td>
									<td className="px-4 py-4 align-top text-sm leading-relaxed text-gray-700 md:px-6 md:py-5 md:text-base">
										{t(`${row.keyPrefix}App`)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{/* Sample Images Grid */}
				<div className="grid grid-cols-2 gap-4 sm:gap-5 md:gap-6 lg:grid-cols-4">
					{SAMPLES.map((sample) => (
						<div
							key={sample.keyPrefix}
							className={`overflow-hidden ${sample.highlight ? "ring-2 ring-cyan-400" : ""}`}
						>
							<div className="relative aspect-square w-full border border-[#e7e7e7] bg-[#f0f0f0]">
								<Image
									src={sample.image}
									alt={t(`${sample.keyPrefix}Alt`)}
									fill
									className="object-cover"
									sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 25vw"
								/>
							</div>
							<div className="pt-3 sm:pt-4">
								<p className="text-xs text-black sm:text-sm">
									<span className="font-bold">{t("surfaceFinishMaterialLabel")}:</span>{" "}
									{t(`${sample.keyPrefix}Material`)}
								</p>
								<p className="mt-0.5 text-xs text-black sm:text-sm">
									<span className="font-bold">{t("surfaceFinishFinishLabel")}:</span>{" "}
									{t(`${sample.keyPrefix}Finish`)}
								</p>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
